/**
 * Custom hook for copilot agent streaming.
 *
 * Sends the analyst's message (or an approval decision) and feeds the stream
 * through the shared chat engine (`src/chat/core`), which decides what each
 * event does to the chat. This hook only owns the Copilot page's side: which
 * store, analytics, onboarding, and the live-call modal.
 */

import { useCallback, useRef } from 'react';
import { useCopilotStore } from '../stores/useCopilotStore';
import { ChatStreamService } from '../../../services/chatStreamService';
import {
  decideSqlApproval,
  decideVoiceCallApproval,
  endTurn,
  flushStreaming as flushChatStreaming,
  processChatEvent,
  stopTurn,
} from '../../../chat/core';
import { trackCopilotMessage, trackCopilotSQLApproval, trackCopilotSessionStart } from '../../../utils/analytics';
import { markProductStep } from '../../../hooks/useProductOnboarding';
import { getTenantIdFromStorage, setOnboardingFirstQIfEmpty } from '../../../utils/onboardingLocalStorage';

const get = useCopilotStore.getState;

export function useCopilotStream() {
  const streamService = useRef(new ChatStreamService());
  const sendMessage = useCallback(async (
    message: string,
    token: string,
    options?: { attachmentIds?: string[]; agentType?: string },
  ) => {
    const tenantId = getTenantIdFromStorage();
    if (!tenantId) {
      useCopilotStore.getState().addMessage({
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Error: Not authenticated',
        timestamp: new Date().toISOString(),
      });
      return;
    }
    const store = useCopilotStore.getState();
    const threadId = store.threadId;

    const userMsgId = `user-${Date.now()}`;
    store.addMessage({
      id: userMsgId,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
      // Tagging the user message with attachment_ids lets the chip
      // strip on the historical message re-resolve which files were
      // referenced in this specific turn.  The store type is augmented
      // separately in useCopilotStore.ts.
      ...(options?.attachmentIds && options.attachmentIds.length
        ? { attachmentIds: options.attachmentIds }
        : {}),
    } as any);
    store.appendTimelineUser(userMsgId);

    store.setIsStreaming(true);
    store.setStreamingContent('');
    store.setStreamingAction('Thinking…');
    store.setInputText('');
    store.setPendingAuditId(null);
    if (store.messages.length <= 1) trackCopilotSessionStart(threadId);
    trackCopilotMessage(threadId);
    // Fire-and-forget: mark first_question_asked on first message sent
    markProductStep('first_question_asked');
    // Store first question for completion modal personalisation
    try {
      setOnboardingFirstQIfEmpty(getTenantIdFromStorage(), message);
    } catch { /* ignore */ }

    try {
      // Capture threadId at send time; the guard in processEvent uses this to
      // drop events that arrive after the user has switched to a different thread.
      const sendingThreadId = threadId;
      await streamService.current.sendMessage(
        message,
        threadId,
        token,
        tenantId,
        (event: any) => processEvent(event, sendingThreadId),
        undefined,
        options?.agentType ?? 'copilot',
        options?.attachmentIds,
      );
    } catch (error: any) {
      console.error('[useCopilotStream] Error:', error);
      const errId = `error-${Date.now()}`;
      useCopilotStore.getState().addMessage({
        id: errId,
        role: 'assistant',
        content: `Error: ${error.message || 'Unknown error occurred'}`,
        timestamp: new Date().toISOString(),
      });
      useCopilotStore.getState().appendTimelineAssistantMessage(errId);
    } finally {
      endTurn(get);
    }
  }, []);

  const sendSQLApproval = useCallback(
    async (
      action: 'approve' | 'edit' | 'reject',
      token: string,
      editedSql?: string,
      reason?: string,
    ) => {
      const threadId = get().threadId;
      const resumeData = decideSqlApproval(get, action, editedSql, reason);
      if (!resumeData || !threadId) return;
      trackCopilotSQLApproval(action, threadId);

      const tenantId = getTenantIdFromStorage();
      if (!tenantId) return;

      try {
        const approvalThreadId = threadId;
        await streamService.current.sendMessage(
          '',
          threadId,
          token,
          tenantId,
          (event: any) => processEvent(event, approvalThreadId),
          resumeData,
          'copilot',
        );
      } catch (error: any) {
        console.error('[useCopilotStream] Approval error:', error);
        const eid = `error-${Date.now()}`;
        useCopilotStore.getState().addMessage({
          id: eid,
          role: 'assistant',
          content: `SQL approval error: ${error.message}`,
          timestamp: new Date().toISOString(),
        });
        useCopilotStore.getState().appendTimelineAssistantMessage(eid);
      } finally {
        endTurn(get);
      }
    },
    [],
  );

  const stopStream = useCallback(() => {
    streamService.current.stop();
    stopTurn(get);
  }, []);

  const sendVoiceCallApproval = useCallback(
    async (
      action: 'approve' | 'edit' | 'reject',
      token: string,
      editedNumber?: string,
      reason?: string,
    ) => {
      const threadId = get().threadId;
      const resumeData = decideVoiceCallApproval(get, action, editedNumber, reason);
      if (!resumeData || !threadId) return;
      // Open the modal *before* resuming. The tool writes its row within
      // a second of the resume, and the modal polls for it — opening
      // after would risk missing the ringing state entirely.
      if (action !== 'reject') get().setVoiceCallModalOpen(true);

      const tenantId = getTenantIdFromStorage();
      if (!tenantId) return;

      try {
        const approvalThreadId = threadId;
        await streamService.current.sendMessage(
          '',
          threadId,
          token,
          tenantId,
          (event: any) => processEvent(event, approvalThreadId),
          resumeData,
          'copilot',
        );
      } catch (error: any) {
        console.error('[useCopilotStream] Voice call approval error:', error);
        const eid = `error-${Date.now()}`;
        useCopilotStore.getState().addMessage({
          id: eid,
          role: 'assistant',
          content: `Call could not be started: ${error.message}`,
          timestamp: new Date().toISOString(),
        });
        useCopilotStore.getState().appendTimelineAssistantMessage(eid);
      } finally {
        useCopilotStore.getState().setIsStreaming(false);
      }
    },
    [],
  );

  return { sendMessage, sendSQLApproval, sendVoiceCallApproval, stopStream };
}

// Exported only so the replay tests can drive them; the app goes through the hook.
export function flushStreaming(auditId?: string | null) {
  flushChatStreaming(get, auditId);
}

export function processEvent(event: any, expectedThreadId: string | null) {
  processChatEvent(get, event, expectedThreadId);
}
